/**
 * SP3 §4 + SP7 §7 — the LIVE derivation. The base balance is recomputed per
 * debounced keystroke from the SAME shared balancer the server uses; when the
 * job publishes any policy/rules/promotion config, the base is then finalized
 * through the SAME deriveTransactionSnapshot core the server recompute uses —
 * what the operator previews IS what accept will decide. No policy config ⇒
 * outcome null, snapshot === plain balance (SP3 behavior).
 */
import {
  computeBalanceSnapshot,
  type BalanceSnapshot, type AllocationRow, type PolicyOutcome,
} from "@capture/shared";
import { deriveTransactionSnapshot, type TransactionSnapshotDoc } from "@capture/rules-engine";
import type { StationTransaction, RemittanceStationPayload } from "../api/client";

export interface LiveDerivation {
  snapshot: BalanceSnapshot;
  outcome: PolicyOutcome | null;
  finalAllocations: AllocationRow[];
}

export function deriveTransactionLive(
  txn: StationTransaction,
  amountOverrides: Record<string, number | null>,
  config: RemittanceStationPayload["config"],
  allocationRows: AllocationRow[],
  now?: Date,
): LiveDerivation {
  // This runs per keystroke on the render-hot path — only assemble the
  // policy-eval docs when the job actually publishes policy config.
  const hasPolicyConfig = !!(config.balancePolicy || config.transactionRules?.length || config.transactionFields?.length);

  const payments: Array<{ cents: number | null; source: "keyed" | "ocr" }> = [];
  const coupons: Array<{ cents: number | null; source: "keyed" | "ocr" }> = [];
  const docs: TransactionSnapshotDoc[] = [];

  for (const doc of txn.documents) {
    const overridden = Object.prototype.hasOwnProperty.call(amountOverrides, doc.id);
    const cents = overridden ? amountOverrides[doc.id]! ?? null : doc.amountCents;
    if (hasPolicyConfig) {
      // Binding contract (spec §2): the eval row's field entry must agree with
      // the amount the balancer sums — patch the amount field on override.
      const fields =
        overridden && doc.amountFieldName
          ? { ...doc.fields, [doc.amountFieldName]: cents === null ? null : (cents / 100).toFixed(2) }
          : doc.fields;
      docs.push({ documentId: doc.id, classificationKey: doc.classification, role: doc.role, amountCents: cents, fields });
    }
    if (doc.role === "payment" || doc.role === "coupon") {
      const reading = { cents, source: (overridden ? "keyed" : "ocr") as "keyed" | "ocr" };
      if (doc.role === "payment") payments.push(reading);
      else coupons.push(reading);
    }
  }

  const base = computeBalanceSnapshot({ payments, coupons, toleranceCents: config.toleranceCents, now });
  if (!hasPolicyConfig) return { snapshot: base, outcome: null, finalAllocations: allocationRows.filter((r) => r.source !== "policy") };

  const result = deriveTransactionSnapshot({
    transaction: { ordinal: txn.ordinal, state: txn.state },
    base,
    docsInOrder: docs,
    allocations: allocationRows,
    config: {
      balancePolicy: config.balancePolicy ?? null,
      transactionRules: config.transactionRules ?? null,
      transactionFields: config.transactionFields ?? null,
    },
  });
  return { snapshot: result.snapshot, outcome: result.outcome, finalAllocations: result.finalAllocations };
}
